import { useApiQuery } from "../use-api-query";

/**
 * Response payload from `GET /client/argus/sessions/{id}/report`.
 *
 * Mirrors the backend shape: `report_md` and `report_s3_key` are null
 * when the agent hasn't written a report yet. `updated_at` is the
 * session's last-updated timestamp (not the report's), handy for a
 * rough "fetched Xs ago" hint in the UI.
 */
export interface ArgusSessionReportData {
  report_md: string | null;
  report_s3_key: string | null;
  updated_at: string | null;
}

interface ArgusSessionReportResponse {
  data: ArgusSessionReportData;
}

/**
 * Fetch the rendered markdown report for an Argus session.
 *
 * The agent writes `report.md` via its `write_report` extension which
 * push-notifies the backend; the backend uploads the file to S3 and
 * stamps `ArgusSession.report_s3_key`. This hook polls the backend so
 * the drawer can show live progress while the session is running and
 * settles on the final report once the session terminates.
 *
 * @param sessionId  cope DB session id (NOT the gateway's agent_session_id)
 * @param enabled    gate fetching (e.g. only while the drawer is open)
 * @param isLive     when true, poll every 5s; when false, stop polling.
 *                   Callers should flip to false once the session is
 *                   terminal so we don't keep hammering a settled row.
 */
export function useArgusSessionReport(
  sessionId: string | undefined,
  enabled: boolean,
  isLive: boolean,
) {
  const query = useApiQuery<ArgusSessionReportResponse>(
    `/client/argus/sessions/${sessionId}/report`,
    {
      enabled: !!sessionId && enabled,
      refetchInterval: isLive ? 5_000 : false,
    },
  );

  return {
    reportMd: query.data?.data?.report_md ?? null,
    reportS3Key: query.data?.data?.report_s3_key ?? null,
    updatedAt: query.data?.data?.updated_at ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
